import {BuyPopUpConstants} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusPopUp/BuyPopUpConstants';
import {
    buyPopUpBetStyle,
    buyPopUpStyle,
    costInRubiesStyle
} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusStyles/Styles';
import {Container, Sprite, Text, Texture} from 'pixi.js';
import {isEnabledRubyCoins} from '@gongxigames/framework/slot/modules/buyBonus/isEnabledRubyCoins';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {
    AdvancedText, AnimationProperty,
    Button,
    CoreDI,
    CurrencyFormatter, FullScreenFader, IAdvancedTextConfig, inject,
    LanguageService,
    ModuleIsEnabledFilter,
    ResolutionManager, ScreenParameters, Spine, State, Tween
} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {GameConfig, ILogic, IReelsScaleService} from '@gongxigames/framework/slot/types';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

export class BuyPopUpView extends View<GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('BuyPopUpPopUpView');
    public static layer = LAYERS.OVER_KEYPAD;
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledRubyCoins;

    public static readonly BUY_BUTTON_CLICKED: symbol = Symbol('BUY_BUTTON_CLICKED');
    public static readonly BUY_POP_CLOSE_CLICKED: symbol = Symbol('BUY_POP_CLOSE_CLICKED');
    public static readonly BUY_PUP_SHOWN: symbol = Symbol('BUY_PUP_SHOWN');
    public static readonly FADE_OUT_ANIMATION_FINISHED: symbol = Symbol('FADE_OUT_ANIMATION_FINISHED');
    public static readonly BET_INC_BUTTON_CLICKED: symbol = Symbol('BET_INC_BUTTON_CLICKED');
    public static readonly BET_DEC_BUTTON_CLICKED: symbol = Symbol('BET_DEC_BUTTON_CLICKED');

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    @inject(CoreDI.ResolutionManager)
    protected _resolutionManager!: ResolutionManager;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    protected _continueButton!: Button;
    protected _spineContainer!: Container;
    protected _spine!: Spine;
    protected _fader!: FullScreenFader;
    protected _advancedTexts: IAdvancedTextConfig[] = [];
    protected _faderAlpha = 0.85;
    protected _spineName = 'buy_popup';

    protected _rubyValueText!: Text;
    protected _realValueText!: Text;
    protected _playAtText!: AdvancedText;
    protected _betText!: Text;
    protected _totalBetText!: AdvancedText;
    protected _plusText!: Text;

    protected _closeButton!: Button;
    protected _decBetButton!: Button;
    protected _incBetButton!: Button;

    protected _buyPopUpSprite!: Sprite;
    protected _costContainer!: Sprite;
    protected _topTextContainer!: Sprite;
    protected _betTextContainer!: Sprite;
    protected _rubySprite!: Sprite;

    public init(): void {
        if (this._gameConfig.buyPopUpTXT) {
            this._advancedTexts = this._gameConfig.buyPopUpTXT;
        }

        super.init();

        this.initFader();
        this.initSpine();
        this.initControls();
        this.initTexts();
        this.hide();
    }

    public playAnimation(): void {
        this.show();
        this.playFaderAnimation();
        this.playPopupAnimation();
        this.soundPlayer.play('rubyBannerUp');
    }

    protected initFader(): void {
        this._fader = this._displayObjectUtils.createFullScreenFader({alpha: this._faderAlpha});

        this._fader.visible = false;
        this.container.addChild(this._fader);
    }

    protected playPopupAnimation(): void {
        const animProperty: AnimationProperty = {
            animationName: this.getAnimationName(),
            trackIndex: 0,
            loop: false,
            listeners: {
                complete: () => {
                    this.emit(BuyPopUpView.BUY_PUP_SHOWN);
                }
            }
        };

        this._spine.setAnimation(animProperty);
        this.container.alpha = 1;

        this.spineUtils.applyMasks(this._spine);
    }

    protected initControls(): void {
        this._topTextContainer = new Sprite();
        this._costContainer = new Sprite();
        this._betTextContainer = new Sprite();

        this._buyPopUpSprite = this.objectBuilder.buildDisplayObjectByName('spineAttachments',
            'buyPopup') as Sprite;

        const bonusBet = this._buyPopUpSprite.getChildByName<Sprite>('bonusBet');

        this._betText = bonusBet.getChildByName<Text>('betText');

        this._decBetButton = bonusBet.getChildByName<Button>('bonusBetMinus');
        this._decBetButton.callback = this.onDecBetButtonClicked.bind(this);

        this._incBetButton = bonusBet.getChildByName<Button>('bonusBetPlus');
        this._incBetButton.callback = this.onIncBetButtonClicked.bind(this);

        this._betTextContainer.addChild(this._betText);

        const bonusCost = this._buyPopUpSprite.getChildByName<Sprite>('bonusCost');

        this._realValueText = bonusCost.getChildByName<Text>('realValueText');
        this._plusText = bonusCost.getChildByName<Text>('plusText');
        this._costContainer.addChild(bonusCost);

        const rubyBonus = this._buyPopUpSprite.getChildByName<Sprite>('rubyBonus');

        this._rubySprite = rubyBonus.getChildByName<Text>('ruby');
        this._rubyValueText = rubyBonus.getChildByName<Text>('rubyValueText');
        this._costContainer.addChild(rubyBonus);

        const closeButtonSprite = this._buyPopUpSprite.getChildByName<Button>('closeButton');

        this._closeButton = closeButtonSprite.getChildByName<Button>('cross_bonus');
        this._closeButton.callback = this.onCloseButtonClicked.bind(this);

        this.updateStyle();

        this.addSpriteToSlot(this._decBetButton, 'arrow_minus_normal', true, true);
        this.addSpriteToSlot(this._incBetButton, 'arrow_plus_normal', true, true);
        this.addSpriteToSlot(this._topTextContainer, 'playbonus_text', false, false);
        this.addSpriteToSlot(this._betTextContainer, 'totalbet_text', false, true);
        this.addSpriteToSlot(this._costContainer, 'popup_text2', false, false);
        this.addSpriteToSlot(this._closeButton, 'popup_cross', true, true);
    }

    protected updateStyle(): void {
        this._betText.anchor.set(0.5);
        this._betText.style = buyPopUpBetStyle as any;

        this._plusText.style = buyPopUpStyle as any;
        this._plusText.anchor.set(0.5);

        this._realValueText.style = buyPopUpStyle as any;
        this._realValueText.anchor.set(0.5);

        this._rubyValueText.style = costInRubiesStyle as any;
        this._rubyValueText.anchor.set(0.5);
    }

    protected initTexts(): void {
        const {BUY_TEXT_ID, PLAY_TEXT_ID, PLAY_AT_TEXT_ID, TOTAL_BET_TEXT_ID, NOT_ENOUGH_RUBY_TEXT_ID} = BuyPopUpConstants;
        const buyTextId = this.getSettings<SlotSettings>().get('ukRegulations') ? PLAY_TEXT_ID : BUY_TEXT_ID;

        this._advancedTexts.forEach(data => {
            if (data.id === buyTextId || data.id === NOT_ENOUGH_RUBY_TEXT_ID) {
                for (const frame of Object.keys(this._continueButton.frames)) {
                    const childSprite = this._continueButton.frames[frame as State] as Sprite;
                    const buyText = childSprite.getChildByName('text');
                    const buyTXTTranslated = new AdvancedText(
                        this.objectBuilder,
                        this._languageService.get(data.id),
                        data.style,
                        this._screenManager.defaultViewportResolution
                    );
                    const buttonBack = childSprite.getChildByName<Button>('btn');

                    buyTXTTranslated.name = data.id;
                    buyTXTTranslated.maxWidth = data.maxWidth;
                    buyTXTTranslated.x = buttonBack.x + buttonBack.width / 2;
                    buyTXTTranslated.y = buttonBack.y + buttonBack.height / 2 - buyTXTTranslated.height / 2;
                    buyText && childSprite.removeChild(buyText);
                    childSprite.addChild(buyTXTTranslated);
                }
            } else if (data.id === PLAY_AT_TEXT_ID) {
                const playBonusAt = this._buyPopUpSprite.getChildByName<Sprite>('playBonusAt');
                const playBonusAtText = playBonusAt.getChildByName<Text>('playAtText');

                this._playAtText = new AdvancedText(
                    this.objectBuilder,
                    this._languageService.get(data.id),
                    data.style,
                    this._screenManager.defaultViewportResolution
                );
                this._playAtText.maxWidth = data.maxWidth;
                this._playAtText.x = 0;
                this._playAtText.y = playBonusAtText.y - playBonusAtText.height / 2;
                this._topTextContainer.addChild(this._playAtText);
            } else if (data.id === TOTAL_BET_TEXT_ID) {
                const bonusBet = this._buyPopUpSprite.getChildByName<Sprite>('bonusBet');
                const totalBetText = bonusBet.getChildByName<Text>('totalBetText');

                this._totalBetText = new AdvancedText(
                    this.objectBuilder,
                    this._languageService.get(data.id),
                    data.style,
                    this._screenManager.defaultViewportResolution
                );
                this._totalBetText.maxWidth = data.maxWidth;
                this._totalBetText.x = 0;
                this._totalBetText.y = totalBetText.y - totalBetText.height / 2;
                this._betTextContainer.addChild(this._totalBetText);
            }
        });
    }

    protected onCloseButtonClicked(): void {
        this.soundPlayer.play('rubyBannerDown');
        this.preparePopUpRemoving();
        this.emit(BuyPopUpView.BUY_POP_CLOSE_CLICKED);
    }

    protected preparePopUpRemoving(): void {
        this.soundPlayer.play('buttonclick');
        this.disableButtons();
        this.playFadeOutAnimation();
    }

    protected onDecBetButtonClicked(): void {
        this.enableBetIncButton();

        this.emit(BuyPopUpView.BET_DEC_BUTTON_CLICKED);
    }

    protected onIncBetButtonClicked(): void {
        this.enableBetDecButton();

        this.emit(BuyPopUpView.BET_INC_BUTTON_CLICKED);
    }

    protected enableBetDecButton(): void {
        this._decBetButton.enable();
    }

    protected enableBetIncButton(): void {
        this._incBetButton.enable();
    }

    protected disableButtons(): void {
        this._closeButton.disable();
        this._decBetButton.disable();
        this._incBetButton.disable();
    }

    public playFadeOutAnimation(): void {
        const fadeOutTween: Tween = new Tween(this.container)
            .to({alpha: 0.0}, 500)
            .onComplete(this.onFadeOutAnimationFinished.bind(this))
            .start();

        this.updateManager.add(fadeOutTween);
    }

    protected initSpine(): void {
        this._spineContainer = new Container();
        this._spine = this.objectBuilder.buildSpine(this._spineName);
        this._spine.x = this._gameConfig.mainFrame.width / 2;
        this._spine.y = this._gameConfig.mainFrame.height / 2;

        this.updateManager.add(this._spine);
        this._spine.start();

        this._spineContainer.addChild(this._spine);
        this.container.addChild(this._spineContainer);

        this._continueButton = this.objectBuilder.buildDisplayObjectByName('spineAttachments', 'buttons.buy') as Button;
        this._continueButton.callback = this.onBuyButtonClicked.bind(this);

        this.addSpriteToSlot(this._continueButton, 'popup_button');

        const animProperty: AnimationProperty = {
            animationName: this.getAnimationName(),
            trackIndex: 0,
            loop: false
        };

        this._spine.state.clearTracks();
        this._spine.setAnimation(animProperty);
    }

    protected addSpriteToSlot(sprite: Sprite, slotName: string, centerX = true, centerY = true): void {
        const slotSprite = this._spine.skeleton.findSlot(slotName).currentSprite;

        this.clearTexture(slotName);
        sprite.x = (centerX ? -sprite.getLocalBounds().width : 1) / 2;
        sprite.y = (centerY ? -sprite.getLocalBounds().height : 1) / 2;

        slotSprite.addChild(sprite);
    }

    protected clearTexture(slotName: string): void {
        const slotSprite = this._spine.skeleton.findSlot(slotName).currentSprite;

        slotSprite.texture = Texture.EMPTY;
        slotSprite.removeChildren();
    }

    protected getAnimationName(): string {
        return 'animation';
    }

    protected onFadeOutAnimationFinished(): void {
        this.hide();
        this.emit(BuyPopUpView.FADE_OUT_ANIMATION_FINISHED);
    }

    protected updateTextPosition(): void {
        this._realValueText.x = this._plusText.x - this._plusText.width - this._realValueText.width / 2;
        this._rubyValueText.x = this._rubySprite.x + this._rubySprite.width + this._rubyValueText.width / 2;
    }

    protected playFaderAnimation(): void {
        this._fader.alpha = 0;

        const tween = new Tween(this._fader)
            .to({alpha: this._faderAlpha}, 500)
            .start();

        this.updateManager.add(tween);
    }

    public show(): void {
        this._fader.visible = true;
        this._spineContainer.visible = true;
    }

    public hide(): void {
        this._fader.visible = false;

        if (this._spineContainer) {
            this._spineContainer.visible = false;
            this._continueButton.disable();
        }
    }

    public updateBuyBonusData(totalBet: number, rubyCost: number, realValueCost?: number): void {
        this._betText.text = this._currencyFormatter.formatCents(totalBet, true);
        this._realValueText.text = this._currencyFormatter.formatCents(realValueCost ? realValueCost : totalBet, true);
        this._rubyValueText.text = String(rubyCost);
        this.updateTextPosition();
    }

    public onBuyButtonClicked(): void {
        this.emit(BuyPopUpView.BUY_BUTTON_CLICKED);

        this._continueButton.disable();
        this.preparePopUpRemoving();
    }

    public disableBuyButton(): void {
        const {BUY_TEXT_ID, NOT_ENOUGH_RUBY_TEXT_ID, PLAY_TEXT_ID} = BuyPopUpConstants;

        this._continueButton.disable();

        for (const frame of Object.keys(this._continueButton.frames)) {
            const childSprite = this._continueButton.frames[frame as State] as Sprite;
            const buyTextId = this.getSettings<SlotSettings>().get('ukRegulations') ? PLAY_TEXT_ID : BUY_TEXT_ID;

            (childSprite.getChildByName<Text>(buyTextId)).visible = false;
            (childSprite.getChildByName<Text>(NOT_ENOUGH_RUBY_TEXT_ID)).visible = true;
        }
    }

    public enableBuyButton(): void {
        const {BUY_TEXT_ID, NOT_ENOUGH_RUBY_TEXT_ID, PLAY_TEXT_ID} = BuyPopUpConstants;

        this._continueButton.enable();

        for (const frame of Object.keys(this._continueButton.frames)) {
            const childSprite = this._continueButton.frames[frame as State] as Sprite;
            const buyTextId = this.getSettings<SlotSettings>().get('ukRegulations') ? PLAY_TEXT_ID : BUY_TEXT_ID;

            (childSprite.getChildByName<Text>(buyTextId)).visible = true;
            (childSprite.getChildByName<Text>(NOT_ENOUGH_RUBY_TEXT_ID)).visible = false;
        }
    }

    public disableBetDecButton(): void {
        this._decBetButton.disable();
    }

    public disableBetIncButton(): void {
        this._incBetButton.disable();
    }

    public enableButtons(): void {
        this._closeButton.enable();
        this._decBetButton.enable();
        this._incBetButton.enable();
    }

    public onLandscape(): void {
        super.onLandscape();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this._spineContainer);
    }

    public onPortrait(): void {
        super.onPortrait();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this._spineContainer);
    }

    public onResized(screenParams: ScreenParameters): void {
        this._fader?.resize(screenParams);
    }
}
